import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getServiceById, updateService, deleteService } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Wrench,
  DollarSign,
  Phone,
  Save,
  AlertCircle,
  Clock,
  Star,
  Plus,
  X,
  Mail,
  Briefcase,
  History,
  CheckCircle2,
  Trash2,
  Tag,
} from "lucide-react";

interface ServiceFormData {
  title: string;
  description: string;
  category: string;
  listingType: "OFFER" | "REQUEST";
  price: string;
  priceType: "HOURLY" | "FIXED" | "PER_SESSION" | "FREE";
  duration: string;
  availability: "AVAILABLE" | "LIMITED" | "BUSY";
  experience: string;
  qualifications: string[];
  contactEmail: string;
  contactPhone: string;
}

const SERVICE_CATEGORIES = [
  { value: "TUTORING", label: "Tutoring" },
  { value: "CLEANING", label: "Cleaning" },
  { value: "REPAIR", label: "Repair" },
  { value: "PERSONAL_CARE", label: "Personal Care" },
  { value: "TRANSPORTATION", label: "Transportation" },
  { value: "EVENT_PLANNING", label: "Event Planning" },
  { value: "PHOTOGRAPHY", label: "Photography" },
  { value: "CONSULTING", label: "Consulting" },
  { value: "OTHER", label: "Other" },
];

export default function EditServicePage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const { data: service, isLoading } = useQuery(getServiceById, { itemId: itemId! });
  const updateServiceFn = useAction(updateService);
  const deleteServiceFn = useAction(deleteService);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newQualification, setNewQualification] = useState("");
  
  const [formData, setFormData] = useState<ServiceFormData>({
    title: "",
    description: "",
    category: "OTHER",
    listingType: "OFFER",
    price: "",
    priceType: "FIXED",
    duration: "",
    availability: "AVAILABLE",
    experience: "",
    qualifications: [],
    contactEmail: "",
    contactPhone: "",
  });

  useEffect(() => {
    if (service) {
      const meta = service.metadata as any;
      setFormData({
        title: service.title || "",
        description: service.description || "",
        category: meta?.category || "OTHER",
        listingType: meta?.listingType || "OFFER",
        price: meta?.price ? meta.price.toString() : "",
        priceType: meta?.priceType || "FIXED",
        duration: meta?.duration || "",
        availability: meta?.availability || "AVAILABLE",
        experience: meta?.experience || "",
        qualifications: meta?.qualifications || [],
        contactEmail: meta?.contactEmail || (meta?.contactInfo?.includes("@") ? meta.contactInfo.split("|")[0].trim() : ""),
        contactPhone: meta?.contactPhone || (meta?.contactInfo?.includes("|") ? meta.contactInfo.split("|")[1].trim() : ""),
      });
    }
  }, [service]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof ServiceFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const addQualification = () => {
    if (newQualification.trim() && !formData.qualifications.includes(newQualification.trim())) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, newQualification.trim()]
      }));
      setNewQualification("");
    }
  };

  const removeQualification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!formData.experience.trim()) newErrors.experience = "Experience is required";
    if (formData.priceType !== "FREE" && !formData.price) newErrors.price = "Price is required";
    if (!selectedLocation?.id) newErrors.location = "No location selected";
    if (formData.contactPhone && formData.contactPhone?.replace(/\D/g, "").length < 10) {
      newErrors.contactPhone = "Please enter a valid 10-digit phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await updateServiceFn({
        itemId: itemId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category as any,
        listingType: formData.listingType,
        price: formData.priceType === "FREE" ? 0 : parseFloat(formData.price),
        priceType: formData.priceType,
        duration: formData.duration.trim(),
        availability: formData.availability,
        experience: formData.experience.trim(),
        qualifications: formData.qualifications,
        contactInfo: `${formData.contactEmail}${formData.contactPhone ? ` | ${formData.contactPhone}` : ""}`,
      });
      handleGoBack();
    } catch (error) {
      console.error("Error updating service:", error);
      setErrors({ submit: "Failed to update service." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await deleteServiceFn({ itemId: itemId! });
      handleGoBack();
    } catch (error) {
      console.error("Error deleting service:", error);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/services`);
    } else {
      navigate("/services");
    }
  };

  if (isLoading || !user || !service) return null;

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="service-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Saving..." : "Save Changes"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Service Status" hideOnMobile>
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Current Availability</label>
        <div className="grid grid-cols-1 gap-2">
          {["AVAILABLE", "LIMITED", "BUSY"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleChange("availability", opt)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all border ${
                formData.availability === opt
                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
              }`}
            >
              {opt.charAt(0) + opt.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </SidebarSection>
      <SidebarSection title="Danger Zone" hideOnMobile>
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 transition-colors font-semibold"
        >
          <Trash2 className="w-4 h-4" /> Delete Service
        </button>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Service"
      subtitle={`Manage ${formData.title}`}
      icon={Wrench}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="service-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
            <button
              type="button"
              onClick={() => handleChange("listingType", "OFFER")}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                formData.listingType === "OFFER" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Briefcase className="w-4 h-4" /> Offering
            </button>
            <button
              type="button"
              onClick={() => handleChange("listingType", "REQUEST")}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                formData.listingType === "REQUEST" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Star className="w-4 h-4" /> Requesting
            </button>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Service Title</label>
            <div className="relative">
              <Wrench className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Classification</h3>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                {SERVICE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Pricing Type</label>
              <div className="flex flex-wrap gap-2">
                {["HOURLY", "FIXED", "PER_SESSION", "FREE"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleChange("priceType", type)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                      formData.priceType === type
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200"
                    }`}
                  >
                    {type?.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            {formData.priceType !== "FREE" && (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Price ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><History className="w-4 h-4 text-blue-600" /> Experience</h3>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Years of Experience</label>
              <input
                type="text"
                value={formData.experience}
                onChange={(e) => handleChange("experience", e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Duration per session</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => handleChange("duration", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Qualifications</h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={newQualification}
              onChange={(e) => setNewQualification(e.target.value)}
              placeholder="Add qualification..."
              className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addQualification())}
            />
            <button
              type="button"
              onClick={addQualification}
              className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.qualifications.map((q, i) => (
              <span key={i} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-bold border border-blue-100">
                {q}
                <button type="button" onClick={() => removeQualification(i)} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Email (tied to account)</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={formData.contactEmail}
                  readOnly
                  className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.contactPhone}
                  onChange={(e) => handleChange("contactPhone", e.target.value)}
                  placeholder="(XXX) XXX-XXXX"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                />
              </div>
            </div>
          </div>
        </div>
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
